import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import Logo from '@/components/Logo';
import ProductCard from '@/components/ProductCard';
import {
  useCatalogue,
  useCatalogueLines,
  useCreateProduct,
  useDebounced,
  useToggleFeatured,
  useVueProduits,
  type CatalogueLine,
  type Filters,
  type Product,
  type Sort,
} from '@/lib/catalogue';
import type { MeResponse } from '@/lib/api';
import { cn } from '@/lib/utils';

const TRIS: { value: Sort; label: string }[] = [
  { value: 'pertinence', label: "Vedettes d'abord" },
  { value: 'part', label: 'N° de pièce' },
  { value: 'description', label: 'Description' },
  { value: 'prix', label: 'Prix croissant' },
];

export default function Catalogue({
  session,
  embedded = false,
}: {
  session: MeResponse;
  embedded?: boolean;
}) {
  const [q, setQ] = useState('');
  const [lineCode, setLineCode] = useState<string | null>(null);
  const [sort, setSort] = useState<Sort>('pertinence');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [vue, setVue] = useVueProduits();

  const debouncedQ = useDebounced(q);
  const filters: Filters = useMemo(
    () => ({ q: debouncedQ, lineCode, sort, featuredOnly }),
    [debouncedQ, lineCode, sort, featuredOnly],
  );

  const { products, total, isPending, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCatalogue(filters);
  const lines = useCatalogueLines();

  // Un admin peut basculer une vedette directement depuis la carte produit.
  const isAdmin = session.user.role === 'ADMIN';
  const toggleFeatured = useToggleFeatured();
  const onToggleFeatured = isAdmin
    ? (p: Product) => toggleFeatured.mutate({ productId: p.id, featured: !p.isFeatured })
    : undefined;

  const mesLignes = lines.data?.lines ?? [];
  const plusieursLignes = mesLignes.length > 1;
  const filtreActif = q !== '' || lineCode !== null || featuredOnly;

  return (
    <div className={embedded ? 'space-y-3' : 'flex min-h-full flex-col'}>
      {/* Intégré au panneau admin (embedded) : on masque le chrome autonome (logo,
          « Accueil », titre) que fournit déjà le layout admin. */}
      <header className={cn('space-y-3', !embedded && 'bg-card sticky top-0 z-10 border-b p-4')}>
        {!embedded && (
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="border-input flex h-14 items-center rounded-lg border px-4 font-medium"
            >
              ← Accueil
            </Link>
            <Logo className="h-9" />
            <h1 className="text-xl font-bold">Catalogue</h1>
            <span className="text-muted-foreground ml-auto text-sm">{session.user.fullName}</span>
          </div>
        )}

        {/* Champ de recherche : 64 px de haut, tapable sans viser. */}
        <div className="relative">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="N° de pièce ou description…"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="border-input bg-background focus:ring-ring h-16 w-full rounded-lg border px-4 pr-28 text-lg focus:ring-2 focus:outline-none"
          />
          {q !== '' && (
            <button
              onClick={() => setQ('')}
              className="text-muted-foreground absolute top-1/2 right-3 h-12 -translate-y-1/2 rounded-lg px-4 text-sm font-medium"
            >
              Effacer
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <FilterChip active={featuredOnly} onClick={() => setFeaturedOnly(!featuredOnly)}>
            ★ Vedettes
          </FilterChip>
          {/* Filtre par ligne : affiché seulement si le rep en a plusieurs. */}
          {plusieursLignes && (
            <>
              <span className="bg-border mx-1 h-8 w-px" />
              <FilterChip active={lineCode === null} onClick={() => setLineCode(null)}>
                Toutes
              </FilterChip>
              {mesLignes.map((l) => (
                <FilterChip
                  key={l.id}
                  active={lineCode === l.code}
                  onClick={() => setLineCode(lineCode === l.code ? null : l.code)}
                >
                  {l.code}
                  <span className="text-muted-foreground ml-1.5 text-xs">{l.productCount}</span>
                </FilterChip>
              ))}
            </>
          )}

          <div className="ml-auto flex items-center gap-2">
            {/* Basculement grille / liste. La liste montre beaucoup plus de
                produits d'un coup ; la préférence est mémorisée. */}
            <div className="border-input flex overflow-hidden rounded-lg border">
              <button
                onClick={() => setVue('grid')}
                aria-pressed={vue === 'grid'}
                className={cn(
                  'h-12 px-3 text-sm font-medium',
                  vue === 'grid' ? 'bg-primary/10 text-foreground' : 'text-muted-foreground',
                )}
              >
                Grille
              </button>
              <button
                onClick={() => setVue('list')}
                aria-pressed={vue === 'list'}
                className={cn(
                  'border-input h-12 border-l px-3 text-sm font-medium',
                  vue === 'list' ? 'bg-primary/10 text-foreground' : 'text-muted-foreground',
                )}
              >
                Liste
              </button>
            </div>

            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as Sort)}
              className="border-input bg-background h-12 rounded-lg border px-3 text-sm"
            >
              {TRIS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      {/* Ajout manuel (admin) : un fournisseur qui apporte un produit hors
          catalogue, sans fichier à importer. */}
      {embedded && <NewProductForm lines={mesLignes} />}

      <div className={embedded ? 'space-y-3 pt-2' : 'flex-1 space-y-3 p-4'}>
        <p className="text-muted-foreground text-sm" aria-live="polite">
          {isPending
            ? 'Chargement…'
            : `${total} produit${total > 1 ? 's' : ''}${filtreActif ? ' correspondent au filtre' : ''}`}
        </p>

        {isError && (
          <div className="border-destructive/40 bg-destructive/10 rounded-lg border p-4">
            <p className="text-destructive font-medium">Catalogue indisponible.</p>
            <p className="text-muted-foreground mt-1 text-sm">{error?.message}</p>
          </div>
        )}

        {!isPending && !isError && products.length === 0 && (
          <div className="rounded-xl border border-dashed p-10 text-center">
            <p className="font-medium">Aucun produit ne correspond.</p>
            <p className="text-muted-foreground mt-1 text-sm">
              {q !== '' ? `Aucun résultat pour « ${q} ».` : 'Ajustez les filtres.'}
            </p>
            {filtreActif && (
              <button
                onClick={() => {
                  setQ('');
                  setLineCode(null);
                  setFeaturedOnly(false);
                }}
                className="border-input mt-4 h-12 rounded-lg border px-5 font-medium"
              >
                Réinitialiser les filtres
              </button>
            )}
          </div>
        )}

        {/* lg (1024 px) et non xl (1280 px) : l'iPad Pro 11 en paysage fait
            1194 px de large. Avec xl, la bascule à deux colonnes ne se
            déclenchait jamais sur l'appareil cible. */}
        {vue === 'list' ? (
          <div
            data-testid="grille-produits"
            className="bg-card divide-y overflow-hidden rounded-xl border"
          >
            {products.map((p) => (
              <ProductCard key={p.id} product={p} view="list" onToggleFeatured={onToggleFeatured} />
            ))}
          </div>
        ) : (
          <div data-testid="grille-produits" className="grid gap-3 lg:grid-cols-2">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} view="grid" onToggleFeatured={onToggleFeatured} />
            ))}
          </div>
        )}

        {hasNextPage && (
          <button
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="border-input h-16 w-full rounded-lg border text-lg font-medium disabled:opacity-50"
          >
            {isFetchingNextPage ? 'Chargement…' : `Afficher plus (${products.length} sur ${total})`}
          </button>
        )}
      </div>
    </div>
  );
}

/** Ajout manuel d'un produit (admin) — repliable, pour un fournisseur sans fichier. */
function NewProductForm({ lines }: { lines: CatalogueLine[] }) {
  const [open, setOpen] = useState(false);
  const create = useCreateProduct();
  const vide = {
    lineId: '',
    partNumber: '',
    description: '',
    uom: '',
    packQty: '1',
    listPrice: '',
    showPrice: '',
    isFeatured: false,
  };
  const [f, setF] = useState(vide);
  const valide = Number(f.lineId) > 0 && f.partNumber.trim() && f.description.trim() && Number(f.listPrice) > 0;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valide) return;
    create.mutate(
      {
        lineId: Number(f.lineId),
        partNumber: f.partNumber.trim(),
        description: f.description.trim(),
        uom: f.uom.trim() || undefined,
        packQty: Number(f.packQty) || 1,
        listPrice: Number(f.listPrice),
        showPrice: f.showPrice.trim() ? Number(f.showPrice) : null,
        isFeatured: f.isFeatured,
      },
      { onSuccess: () => setF(vide) },
    );
  }

  const champ = 'border-input bg-background h-10 rounded-lg border px-3 text-sm';
  return (
    <section className="bg-card mb-3 rounded-xl border">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between p-4 text-left font-semibold"
      >
        <span>+ Ajouter un produit manuellement</span>
        <span className="text-muted-foreground">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <form onSubmit={submit} className="space-y-3 border-t p-4">
          <p className="text-muted-foreground text-sm">
            Pour un fournisseur qui apporte un produit hors catalogue, sans fichier. Un n° de pièce
            déjà existant sur la ligne sera mis à jour.
          </p>
          <div className="flex flex-wrap gap-3">
            <label className="space-y-1">
              <span className="block text-xs font-medium">Ligne *</span>
              <select
                value={f.lineId}
                onChange={(e) => setF({ ...f, lineId: e.target.value })}
                className={champ}
                required
              >
                <option value="">—</option>
                {lines.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.code} — {l.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="block text-xs font-medium">N° de pièce *</span>
              <input
                value={f.partNumber}
                onChange={(e) => setF({ ...f, partNumber: e.target.value })}
                className={`${champ} w-40 font-mono`}
                required
              />
            </label>
            <label className="space-y-1 flex-1">
              <span className="block text-xs font-medium">Description *</span>
              <input
                value={f.description}
                onChange={(e) => setF({ ...f, description: e.target.value })}
                className={`${champ} w-full min-w-56`}
                required
              />
            </label>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="space-y-1">
              <span className="block text-xs font-medium">Unité</span>
              <input
                value={f.uom}
                onChange={(e) => setF({ ...f, uom: e.target.value })}
                placeholder="EA"
                className={`${champ} w-20`}
              />
            </label>
            <label className="space-y-1">
              <span className="block text-xs font-medium">Paquet</span>
              <input
                type="number"
                min={1}
                value={f.packQty}
                onChange={(e) => setF({ ...f, packQty: e.target.value })}
                className={`${champ} w-24`}
              />
            </label>
            <label className="space-y-1">
              <span className="block text-xs font-medium">Prix de liste ($) *</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={f.listPrice}
                onChange={(e) => setF({ ...f, listPrice: e.target.value })}
                className={`${champ} w-32`}
                required
              />
            </label>
            <label className="space-y-1">
              <span className="block text-xs font-medium">Prix show ($)</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={f.showPrice}
                onChange={(e) => setF({ ...f, showPrice: e.target.value })}
                placeholder="(optionnel)"
                className={`${champ} w-32`}
              />
            </label>
            <label className="flex h-10 items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={f.isFeatured}
                onChange={(e) => setF({ ...f, isFeatured: e.target.checked })}
                className="size-4"
              />
              Vedette
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={!valide || create.isPending}
              className="bg-primary text-primary-foreground h-10 rounded-lg px-5 text-sm font-medium disabled:opacity-40"
            >
              {create.isPending ? 'Ajout…' : 'Ajouter le produit'}
            </button>
            {create.isError && (
              <p className="text-destructive text-sm font-medium">{(create.error as Error).message}</p>
            )}
            {create.isSuccess && !create.isPending && (
              <p className="text-sm font-medium text-green-700 dark:text-green-400">Produit ajouté.</p>
            )}
          </div>
        </form>
      )}
    </section>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'h-12 rounded-lg border px-4 font-medium transition-colors',
        active ? 'border-primary bg-primary/10' : 'border-input',
      )}
    >
      {children}
    </button>
  );
}
